"use client";
import Link from "next/link";

import { PageFormProps } from "../components/formInterfaces";
import { PageFormTemplate } from "../components/PageFormTemplate";

import { FormInput } from "@/components/forms/components/FormInputs";
import { forgotPasswordAction } from "@/lib/actions/auth";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";
import { buildUrl } from "@/lib/utils/url";
import {
  ForgotPasswordData,
  forgotPasswordSchema,
} from "@/lib/validations/auth";

interface ForgotPasswordFormProps
  extends Omit<PageFormProps<ForgotPasswordData>, "onSubmit"> {
  forgotPassword: typeof forgotPasswordAction;
}

export function ForgotPasswordForm({
  forgotPassword,
  ...props
}: ForgotPasswordFormProps) {
  return (
    <PageFormTemplate<ForgotPasswordData>
      callbackUrl={buildUrl(routes.auth.login())}
      defaultValues={{
        email: "",
      }}
      description="Enter your email address and we'll send you a link to reset your password"
      eventKeys={{
        attempt: EventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_ATTEMPT,
        failure: EventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_FAILURE,
        success: EventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_SUCCESS,
      }}
      formSchema={forgotPasswordSchema}
      submitLabel="Send Reset Link"
      title="Forgot Password"
      onSubmit={async (data: ForgotPasswordData) => {
        return await forgotPassword(data.email);
      }}
      {...props}
    >
      {({ control }) => (
        <>
          <FormInput
            control={control}
            label="Email"
            name="email"
            placeholder="Enter your email address"
            type="email"
          />

          <div className="bg-default-50 border border-default-200 rounded-md p-3 text-sm">
            <p>
              Enter the email address associated with your account, and we'll
              email you a link to reset your password.
            </p>
          </div>

          <div className="text-center text-sm">
            <Link className="text-primary" href={routes.auth.login()}>
              Back to login
            </Link>
          </div>
        </>
      )}
    </PageFormTemplate>
  );
}
